import { Injectable } from '@angular/core';
import { logFeature } from '@office-start/telemetry-service';
import { KnownFeatureNames } from '@office-start/telemetry-types';
import { filter, take } from 'rxjs/operators';

import { InstallConfigService } from '../install/install-config.service';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { ObservableScope } from '../shared/models/observable-scope.enum';
import { DocumentService } from '../shared/services/document.service';
import { EventService } from '../shared/services/event.service';
import { PerformanceService } from '../shared/services/performance.service';
import { SchedulePriority, SchedulerService } from '../shared/services/scheduler.service';
import { LaunchAction, UrlLauncherService } from '../shared/services/url-launcher.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { UrlParser } from '../shared/utilities/url-parser.utility';

declare let Dime: any;

export const enum DimeExperience {
    BuyButton = 'buybutton',
    InstallGoPremiumButton = 'installbutton',
    ShellGoPremiumButton = 'gopremium',
    ShellBenefitsButton = 'benefits',
    PremiumTemplates = 'templates',
    Fre = 'fre',
    WelcomeBadge = 'welcomebadge'
}

interface DimeExperienceConfig {
    experiences: Array<string>;
}

interface DimeLaunchConfig {
    experience: string;
    campaignId?: string;
    parentElementId?: string;
}

const enum DimeTaskStatus {
    Timeout,
    Completed,
    Disabled
}

@Injectable({
    providedIn: 'root'
})
export class UpsellDataService {
    private upsellTrackingParam: string = '';
    private dime: any;
    private dimeDisabled: boolean = false;
    private loadDimePromise: Promise<DimeTaskStatus>;
    private readonly preloadedExperiences: Array<DimeExperience> = [];

    constructor(
        private readonly eventService: EventService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly contextService: ContextService,
        private readonly documentService: DocumentService,
        private readonly installConfigService: InstallConfigService,
        private readonly instrumentationService: InstrumentationService,
        private readonly performanceService: PerformanceService,
        private readonly schedulerService: SchedulerService,
        private readonly urlLauncherService: UrlLauncherService
    ) {
    }

    /**
     * Get device OEM identifier and create an upsell tracking param
     */
    public getUpsellTrackingParam(): string {
        if (this.upsellTrackingParam !== '') {
            return this.upsellTrackingParam;
        }

        if (!this.capabilitiesService.capabilities.isPwaInUwp) {
            this.upsellTrackingParam = 'HomePage';

            // TODO: simplyfy logic for 'rfm' once the experiment is completed.
            const officeHomeOrigins = ['RFMGraceToOnline', 'RFMGraceToOnline2', 'CovidLockscreen', 'CovidStartMenu', 'CovidAMC', 'CovidActionCenter', 'RFMSubExpB', 'RFMSubExpC', 'EduExp1', 'EduExp2', 'HVAStartMenu', 'MacWXPOO', 'GetGenuine', 'FreeOfframpFromDialog', 'FreeOfframpFromTBA', '5DayPassEndedBizbar', 'GenericRFMBizbar'];
            officeHomeOrigins.forEach(origin => {
                if (this.checkOrigin(origin)) {
                    this.upsellTrackingParam += '-' + (origin.startsWith('RFM') ? 'RFM' : origin);
                }
            });
        } else {
            this.upsellTrackingParam = 'PWA';
            const pwaOrigins = ['WordPad', 'CovidLockscreen', 'CovidStartMenu', 'CovidAMC', 'CovidActionCenter', 'HVAStartMenu', 'CGEAwareness'];
            pwaOrigins.forEach(origin => {
                if (this.checkOrigin(origin)) {
                    this.upsellTrackingParam += '-' + origin;
                }
            });
        }

        return this.upsellTrackingParam;
    }

    public broadcastUpsell() {
        this.eventService.broadcastEvent({
            name: AngularEvents.premiumTemplatesUpsellStarted,
            args: []
        });
    }

    /**
     * Warm up dime for the given dime experience id
     */
    public warmupDime(dimeExperience: DimeExperience) {
        if (this.clientStartupConfigService.config.authType !== AuthType.MSA || this.capabilitiesService.capabilities.isPwaInUwp && window.prelaunchMode) {
            return;
        }

        // Return if the experience has already been preloaded before
        if (this.preloadedExperiences.indexOf(dimeExperience) !== -1) {
            return;
        }

        this.preloadedExperiences.push(dimeExperience);
        this.logDimeEvent(KnownInstrumentationEvents.DimeWarmup, dimeExperience);
        this.scheduleWarmupTask({ experiences: [dimeExperience] });
    }

    /**
     * Launch dime or redirect to product page in case of error or timeout
     */
    public launchDime(dimeExperience: DimeExperience, targetUrl: string, parentElementId?: string ): Promise<void> {
        if (this.dimeDisabled) {
            if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                import('@office-start/launcher-service').then(
                    launchSvc => {
                        launchSvc.launchUrl(targetUrl, {action: 'Upsell'});
                    }
                );
            } else {
                this.urlLauncherService.openUrl(targetUrl, LaunchAction.Upsell);
            }

            this.logDimeEvent(KnownInstrumentationEvents.DimeLaunch, dimeExperience, 'Failed', 'RedirectToPOC;DimeDisabled');
            return;
        }

        let timeoutId = null;
        const loadDimeTask = this.loadDime();
        const timeoutTask = new Promise<DimeTaskStatus>(resolve => {
            timeoutId = setTimeout(() => {
                resolve(DimeTaskStatus.Timeout); }, this.installConfigService.config.dimeTimeoutMs);
            });

        return Promise.race([loadDimeTask, timeoutTask]).then(status => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            // This is an edge case when dime is not loaded in 5 seconds. User will be redirect to POC when click again.
            if (status !== DimeTaskStatus.Completed ) {
                this.dimeDisabled = true;
                this.logDimeEvent(KnownInstrumentationEvents.DimeLaunch, dimeExperience, 'Failed', 'TimeOut');
                return;
            }

            try {
                const ocid = this.getCampainIdFromTargetUrl(targetUrl);
                const launchConfig: DimeLaunchConfig = {
                    experience: dimeExperience,
                    campaignId: ocid
                };

                if (parentElementId) {
                    launchConfig.parentElementId = parentElementId;
                }

                this.logDimeEvent(KnownInstrumentationEvents.DimeLaunch, dimeExperience, 'Start');
                this.eventService.broadcastEvent({
                    name: AngularEvents.dimeUpsellDialogOpened,
                    args: []
                });

                this.dime.launch(launchConfig, () => {
                    this.logDimeEvent(KnownInstrumentationEvents.DimeTransactionCompleted, dimeExperience);
                    this.eventService.broadcastEvent({
                        name: AngularEvents.convertedToPaid,
                        args: []
                    });
                })
                .then(dimeResult => {
                    this.logDimeEvent(KnownInstrumentationEvents.DimeCompleted, dimeExperience, dimeResult.status, dimeResult.metaData);
                    this.eventService.broadcastEvent({
                        name: AngularEvents.dimeUpsellDialogClosed,
                        args: []
                    });
                });
            } catch (e) {
                this.dimeDisabled = true;
                this.logDimeEvent(KnownInstrumentationEvents.DimeLaunch, dimeExperience, 'Failed', e, 'RedirectToPOC;LaunchException');
                if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                    import('@office-start/launcher-service').then(
                        launchSvc => {
                            launchSvc.launchUrl(targetUrl, {action: 'Upsell'});
                        }
                    );
                } else {
                    this.urlLauncherService.openUrl(targetUrl, LaunchAction.Upsell);
                }
            }
        });
    }

    private scheduleWarmupTask(experienceConfig: DimeExperienceConfig): Promise<void> {
        return this.eventService.events.withScope(ObservableScope.PastAndFuture)
                .pipe(filter(event => event.name === AngularEvents.mruFailed || event.name === AngularEvents.mruReady), take(1))
                .toPromise()
                .then(() => {
                    this.schedulerService.scheduleWork(() => {
                        this.loadDime().then(status => {
                            if (status === DimeTaskStatus.Completed) {
                                this.preloadDimeExperience(experienceConfig);
                            }
                        });
                    }, SchedulePriority.VeryLow);
                });
    }

    /**
     * Download dime.js and initialize dime for in-app purchase
     */
    private loadDime(): Promise<DimeTaskStatus> {
        if (this.loadDimePromise) {
            return this.loadDimePromise;
        }

        this.performanceService.mark('LoadDimeScript_Started');
        this.loadDimePromise = new Promise<DimeTaskStatus>(resolve => {
            this.documentService.addScriptToPage(
                this.installConfigService.config.dimeJSUrl,
                () => {
                    this.logDimeEvent(KnownInstrumentationEvents.DimeLoadScript, '', 'Succeeded');
                    this.performanceService.mark('LoadDimeScript_Completed');
                    this.initializeDime()
                    .then(() => {
                        resolve(DimeTaskStatus.Completed);
                    })
                    .catch(() => {
                        resolve(DimeTaskStatus.Disabled);
                    });
                },
                () => {
                    this.logDimeEvent(KnownInstrumentationEvents.DimeLoadScript, '', 'Failed');
                    resolve(DimeTaskStatus.Disabled);
                },
                false,
                'dimeScript'
            );
        });

        return this.loadDimePromise;
    }

    private checkOrigin(requestOrigin: string): boolean {
        return this.clientStartupConfigService.config.sharedClientStartupContext.requestOrigin.toLowerCase() === requestOrigin.toLowerCase();
    }

    private getCampainIdFromTargetUrl(url: string): string {
        if (url && url.indexOf('ocid=') > 0) {
            const location = UrlParser.getLocation(url);
            const params = UrlParser.getQueryParams(location.search);
            return params['ocid'] !== '' ? params['ocid'] : 'PROD_OfficeCom';
        }

        return 'PROD_OfficeCom';
    }

    private initializeDime(): Promise<DimeTaskStatus> {
        return new Promise<DimeTaskStatus>((resolve, reject) => {
            if (this.dime) {
                resolve(DimeTaskStatus.Completed);
            }

            if (this.dimeDisabled) {
                resolve(DimeTaskStatus.Disabled);
            }

            if (typeof(Dime) !== 'undefined') {
                this.performanceService.mark('DimeInitialize_Start');
                const dimeConfig = {
                    partnerId: this.capabilitiesService.capabilities.pwaEnabled ? 'officepwa' : 'officedotcom',
                    language: this.contextService.sharedContext.officeMarket,
                    sessionId: this.clientStartupConfigService.config.sharedClientStartupContext.sessionId,
                    authHint: this.capabilitiesService.capabilities.pwaEnabled ? this.clientStartupConfigService.config.puid : this.clientStartupConfigService.config.userPrincipalName,
                    userInfo: {
                        puid: this.clientStartupConfigService.config.puid
                    }
                };

                try {
                    this.dime = new Dime(dimeConfig);
                    this.performanceService.mark('DimeInitialize_Complete');
                    this.logDimeEvent(KnownInstrumentationEvents.DimeInitialize, '', 'Succeeded');
                    resolve(DimeTaskStatus.Completed);
                } catch (e) {
                    this.dimeDisabled = true;
                    this.logDimeEvent(KnownInstrumentationEvents.DimeInitialize, '', 'Failed', e);
                    reject();
                }
            } else {
                this.dimeDisabled = true;
                this.logDimeEvent(KnownInstrumentationEvents.DimeInitialize, '', 'Failed', 'Dime_Undefined');
                reject();
            }
        });
    }

    private preloadDimeExperience(experienceConfig: DimeExperienceConfig) {
        if (this.dime) {
            this.performanceService.mark('DimePreload_Start');
            this.logDimeEvent(KnownInstrumentationEvents.DimePreload, experienceConfig.experiences.toString(), 'Start');
            this.dime.preload(experienceConfig)
            .catch(err => this.logDimeEvent(KnownInstrumentationEvents.DimePreload, experienceConfig.experiences.toString(), 'Failed', err));
        }
    }

    private logDimeEvent(eventName: KnownInstrumentationEvents,  dimeExperience: string = '', result: string = '', metaData?: any, extraInfo?: string) {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.DimeExperience] = dimeExperience;
        params[InstrumentationParam.State] = result;

        const debugInfo = extraInfo ? extraInfo + ';' : '';
        if (typeof metaData === 'string') {
            params[InstrumentationParam.DebugInfo] = debugInfo + metaData ;
        } else {
            params[InstrumentationParam.DebugInfo] = !!metaData ? debugInfo + JSON.stringify(metaData) : debugInfo;
        }

        this.instrumentationService.logGenericEvent(eventName, params);
        logFeature({
            featureName: KnownFeatureNames.Upsell,
            eventName,
            area: 'Other',
            params: {
                DimeExperience: dimeExperience,
                State: result,
                DebugInfo: typeof metaData === 'string' ? debugInfo + metaData : (!!metaData ? debugInfo + JSON.stringify(metaData) : debugInfo)
            }
        });
    }
}
